import React from 'react';
import { Avatar, Box, Typography, alpha, useTheme } from '@mui/material';
import { IPodMessage } from '../../../interfaces/ICommunityHub';
import { getAvatarGradient } from '../../../helpers/avatarColor';
import type { PodService } from '../../../services/podService';
import type { PodHubService } from '../../../services/podHubService';
import PodMessageReactions from './PodMessageReactions';

interface ChatMessageProps {
  message: IPodMessage;
  currentUserId: string;
  podId: string;
  podService: PodService;
  podHubService: PodHubService;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, currentUserId, podId, podService, podHubService }) => {
  const theme = useTheme();
  const isSystem = message.type === 'system' || message.senderId === null;
  const isOwn = !isSystem && message.senderId === currentUserId;

  if (isSystem) {
    return (
      <Box sx={{ textAlign: 'center', py: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {message.body}
        </Typography>
      </Box>
    );
  }

  if (isOwn) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 1.25 }}>
        <Box
          sx={{
            maxWidth: '70%',
            px: 1.75,
            py: 1,
            // Asymmetric radius anchors the bubble to its sender's side.
            borderRadius: '16px 16px 4px 16px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.55, wordBreak: 'break-word' }}>
            {message.body}
          </Typography>
        </Box>
        {!message.id.startsWith('opt-') && <PodMessageReactions key={`${podId}:${message.id}:${currentUserId}`} podId={podId} messageId={message.id}
          currentUserId={currentUserId} canReact={false} service={podService} hub={podHubService} />}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
      <Avatar
        src={message.avatarUrl ?? undefined}
        sx={{
          width: 28,
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 800,
          flexShrink: 0,
          background: message.avatarUrl ? undefined : getAvatarGradient(message.senderId ?? message.displayName ?? '?'),
          color: theme.palette.grey[50],
        }}
      >
        {(message.displayName ?? '?').charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ maxWidth: '70%' }}>
        {message.displayName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 700, fontSize: '0.72rem' }}>
            {message.displayName}
          </Typography>
        )}
        <Box
          sx={{
            px: 1.75,
            py: 1,
            borderRadius: '4px 16px 16px 16px',
            bgcolor: alpha(theme.palette.grey[500], 0.1),
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.55, wordBreak: 'break-word' }}>
            {message.body}
          </Typography>
        </Box>
        <PodMessageReactions key={`${podId}:${message.id}:${currentUserId}`} podId={podId} messageId={message.id}
          currentUserId={currentUserId} canReact service={podService} hub={podHubService} />
      </Box>
    </Box>
  );
};

export default ChatMessage;
